import {
  ArrowLeftIcon,
  Calendar02Icon,
  Clock01Icon,
  Loading01Icon,
  MapPinIcon,
  QrCode01Icon,
  Settings01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { format, formatDistanceToNow } from 'date-fns';
import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAssetInfo } from '@/components/features/assets/hooks';
import { ParametersChartTab } from '@/components/features/assets/components/ParametersChartTab';
import { useAssetStatusSegments } from '@/components/features/analytics/hooks/use-asset-status-segments';
import { AssetQrCard } from '@/components/features/assets/asset-qr-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  installedPartsApi,
  maintenanceTicketsApi,
  preventiveMaintenanceSchedulesApi,
} from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { z } from 'zod';

type TimePreset = '24h' | '7d' | '30d' | 'custom';

const assetDetailSearchSchema = z.object({
  preset: z.enum(['24h', '7d', '30d', 'custom']).optional(),
  tab: z.enum(['overview', 'parameters']).optional(),
  startDate: z.string().optional(),
  startTime: z.string().optional(),
  endDate: z.string().optional(),
  endTime: z.string().optional(),
});

type AssetDetailSearch = z.infer<typeof assetDetailSearchSchema>;

function getStatusAccent(status: string | null) {
  const s = status?.toLowerCase() || '';
  if (s === 'operational' || s === 'running')
    return {
      dot: 'bg-emerald-500',
      text: 'text-emerald-600 dark:text-emerald-400',
      bg: 'bg-emerald-500/10',
    };
  if (s === 'warning' || s === 'maintenance')
    return {
      dot: 'bg-amber-500',
      text: 'text-amber-600 dark:text-amber-400',
      bg: 'bg-amber-500/10',
    };
  if (s === 'error' || s === 'critical' || s === 'offline' || s === 'stopped')
    return {
      dot: 'bg-rose-500',
      text: 'text-rose-600 dark:text-rose-400',
      bg: 'bg-rose-500/10',
    };
  return {
    dot: 'bg-slate-500',
    text: 'text-slate-600 dark:text-slate-400',
    bg: 'bg-slate-500/10',
  };
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${Math.round(seconds)}s`;
}

function useTimeRange(
  preset: TimePreset,
  customStart: string,
  customStartTime: string,
  customEnd: string,
  customEndTime: string,
) {
  return useMemo(() => {
    const now = new Date();
    if (preset === 'custom') {
      // Build Date objects from local date+time inputs, then send as ISO
      // This ensures the server receives a UTC timestamp that matches
      // what the user selected in their local timezone
      const start = customStart
        ? new Date(`${customStart}T${customStartTime}:00`).toISOString()
        : undefined;
      const end = customEnd
        ? new Date(`${customEnd}T${customEndTime}:59`).toISOString()
        : undefined;
      return { start, end };
    }
    const d = new Date();
    if (preset === '24h') d.setHours(d.getHours() - 24);
    else if (preset === '7d') d.setDate(d.getDate() - 7);
    else if (preset === '30d') d.setDate(d.getDate() - 30);
    return { start: d.toISOString(), end: now.toISOString() };
  }, [preset, customStart, customStartTime, customEnd, customEndTime]);
}

function AssetDetailPage() {
  const { assetId } = Route.useParams();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const [showQrCard, setShowQrCard] = useState(false);

  // Time range from URL search params (persisted across refresh, shareable)
  const preset: TimePreset = search.preset ?? '7d';
  const activeTab = search.tab ?? 'overview';
  const customStart = search.startDate ?? '';
  const customStartTime = search.startTime ?? '00:00';
  const customEnd = search.endDate ?? '';
  const customEndTime = search.endTime ?? '23:59';

  const setSearch = (patch: Partial<AssetDetailSearch>) => {
    navigate({ search: (prev: AssetDetailSearch) => ({ ...prev, ...patch }) });
  };

  const timeRange = useTimeRange(preset, customStart, customStartTime, customEnd, customEndTime);

  // Data
  const { data: assetInfoData, isLoading, error } = useAssetInfo(assetId);
  const { data: segmentsData, isLoading: segmentsLoading } = useAssetStatusSegments(
    assetId,
    timeRange.start,
    timeRange.end,
  );
  const { data: ticketsData, isLoading: ticketsLoading } = useQuery({
    queryKey: [
      'maintenance-tickets',
      { assetId, startDate: timeRange.start, endDate: timeRange.end, limit: 50 },
    ],
    queryFn: () =>
      maintenanceTicketsApi.getAll({
        assetId,
        startDate: timeRange.start,
        endDate: timeRange.end,
        dateField: 'createdAt',
        limit: 50,
      }),
  });
  const { data: partsData, isLoading: partsLoading } = useQuery({
    queryKey: ['installed-parts', { assetId }],
    queryFn: () => installedPartsApi.getAll({ assetId }),
  });
  const { data: schedulesData, isLoading: schedulesLoading } = useQuery({
    queryKey: ['preventive-maintenance-schedules', { assetId, enabled: true }],
    queryFn: () => preventiveMaintenanceSchedulesApi.getAll({ assetId, enabled: true }),
  });

  // Normalize parts data (API may return array or { data: array })
  const partsList = useMemo(() => {
    if (!partsData) return [];
    if (Array.isArray(partsData)) return partsData;
    if ((partsData as any)?.data && Array.isArray((partsData as any).data))
      return (partsData as any).data;
    return [];
  }, [partsData]);

  // Filter parts by time range (client-side)
  const filteredParts = useMemo(() => {
    if (!timeRange.start || !timeRange.end) return partsList;
    const start = new Date(timeRange.start).getTime();
    const end = new Date(timeRange.end).getTime();
    return partsList.filter((part: any) => {
      const t = part.installedAt ? new Date(part.installedAt).getTime() : 0;
      return t >= start && t <= end;
    });
  }, [partsList, timeRange.start, timeRange.end]);

  const tickets = ticketsData?.data ?? [];
  const scheduleList = Array.isArray(schedulesData)
    ? schedulesData
    : ((schedulesData as any)?.data ?? []);
  const assetSegments = segmentsData?.data?.[0];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <HugeiconsIcon
          icon={Loading01Icon}
          className="w-8 h-8 animate-spin text-muted-foreground/40"
        />
      </div>
    );
  }

  if (error || !assetInfoData) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Failed to load asset details</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate({ to: '/assets' })}
            className="gap-2"
          >
            <HugeiconsIcon icon={ArrowLeftIcon} className="w-4 h-4" />
            Back to Assets
          </Button>
        </div>
      </div>
    );
  }

  const { asset } = assetInfoData;
  const accent = getStatusAccent(asset.statusName || asset.statusId);
  const imageUrl = (asset as any).imageUrl;

  return (
    <div className="flex flex-col gap-3 h-[calc(100dvh-7.5rem)] overflow-hidden">
      {/* Hero Header with image backdrop */}
      <div className="relative shrink-0 rounded-xl overflow-hidden border border-border/50">
        {/* Background image or gradient */}
        {imageUrl ? (
          <>
            <img src={imageUrl} alt={asset.name} className="w-full h-36 sm:h-40 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent" />
          </>
        ) : (
          <div
            className={cn(
              'w-full h-36 sm:h-40',
              'bg-gradient-to-br',
              accent.dot.includes('emerald') && 'from-emerald-500/10 via-muted/30 to-muted',
              accent.dot.includes('amber') && 'from-amber-500/10 via-muted/30 to-muted',
              accent.dot.includes('rose') && 'from-rose-500/10 via-muted/30 to-muted',
              accent.dot.includes('slate') && 'from-slate-500/10 via-muted/30 to-muted',
            )}
          />
        )}

        {/* Content overlay */}
        <div className="absolute inset-0 flex flex-col justify-between p-4">
          {/* Top row: nav + QR */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate({ to: '/assets' })}
              className="gap-1.5 text-foreground/80 hover:text-foreground shrink-0 h-7 px-2 bg-background/40 backdrop-blur-sm"
            >
              <HugeiconsIcon icon={ArrowLeftIcon} className="w-4 h-4" />
              <span className="hidden sm:inline text-xs">Assets</span>
            </Button>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="gap-1.5 h-7 px-2.5 bg-background/60 backdrop-blur-sm"
                onClick={() => setShowQrCard(true)}
              >
                <HugeiconsIcon icon={QrCode01Icon} className="w-3.5 h-3.5" />
                <span className="text-xs">QR</span>
              </Button>
            </div>
          </div>

          {/* Bottom row: asset info */}
          <div className="flex items-end justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-xl font-bold truncate">{asset.name}</h1>
                <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-border/50 bg-background/60 backdrop-blur-sm shrink-0">
                  <span className={cn('w-1.5 h-1.5 rounded-full animate-pulse', accent.dot)} />
                  <span
                    className={cn('text-[10px] font-semibold uppercase tracking-wide', accent.text)}
                  >
                    {asset.statusName || asset.statusId || 'Unknown'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 text-xs text-muted-foreground flex-wrap">
                <span className="font-mono tracking-tight">{asset.code}</span>
                {asset.assetType && (
                  <>
                    <span className="hidden sm:inline text-muted-foreground/30">·</span>
                    <span className="hidden sm:inline capitalize">
                      {(asset.assetType as string).replace('_', ' ')}
                    </span>
                  </>
                )}
                {asset.location && (
                  <>
                    <span className="text-muted-foreground/30">·</span>
                    <span className="flex items-center gap-1">
                      <HugeiconsIcon icon={MapPinIcon} className="w-3 h-3" />
                      {asset.location}
                    </span>
                  </>
                )}
                {asset.description && (
                  <>
                    <span className="text-muted-foreground/30 hidden sm:inline">·</span>
                    <span className="hidden sm:inline truncate max-w-[200px]">
                      {asset.description}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Time Range Filter */}
      <div className="space-y-2 shrink-0">
        <div className="flex items-center gap-1.5 sm:gap-2">
          {(['24h', '7d', '30d'] as TimePreset[]).map((p) => (
            <Button
              key={p}
              variant={preset === p ? 'default' : 'outline'}
              size="sm"
              className="h-7 px-2 sm:px-2.5 text-xs"
              onClick={() => setSearch({ preset: p })}
            >
              <span className="sm:hidden">{p}</span>
              <span className="hidden sm:inline">
                {p === '24h' ? 'Last 24h' : p === '7d' ? 'Last 7d' : 'Last 30d'}
              </span>
            </Button>
          ))}
          <Button
            variant={preset === 'custom' ? 'default' : 'outline'}
            size="sm"
            className="h-7 px-2 sm:px-2.5 text-xs"
            onClick={() => {
              const patch: Partial<AssetDetailSearch> = { preset: 'custom' };
              if (!customStart) {
                const d = new Date();
                d.setDate(d.getDate() - 7);
                patch.startDate = d.toISOString().split('T')[0];
                patch.endDate = new Date().toISOString().split('T')[0];
              }
              setSearch(patch);
            }}
          >
            Custom
          </Button>
        </div>
        {preset === 'custom' && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Input
                type="date"
                value={customStart}
                onChange={(e) => setSearch({ startDate: e.target.value })}
                className="h-7 flex-1 min-w-0 text-xs"
              />
              <Input
                type="time"
                value={customStartTime}
                onChange={(e) => setSearch({ startTime: e.target.value })}
                className="h-7 w-[90px] sm:w-[100px] text-xs"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <Input
                type="date"
                value={customEnd}
                onChange={(e) => setSearch({ endDate: e.target.value })}
                className="h-7 flex-1 min-w-0 text-xs"
              />
              <Input
                type="time"
                value={customEndTime}
                onChange={(e) => setSearch({ endTime: e.target.value })}
                className="h-7 w-[90px] sm:w-[100px] text-xs"
              />
            </div>
          </div>
        )}
      </div>

      {/* Status Timeline */}
      <Card className="shrink-0 border-muted/50 bg-card/30 backdrop-blur-sm">
        <CardContent className="p-3">
          {segmentsLoading ? (
            <div className="flex items-center justify-center h-20">
              <HugeiconsIcon
                icon={Loading01Icon}
                className="w-5 h-5 animate-spin text-muted-foreground/40"
              />
            </div>
          ) : assetSegments?.segments?.length ? (
            <StatusTimeline
              segments={assetSegments.segments}
              statusSummary={assetSegments.statusSummary}
              timeRange={timeRange}
            />
          ) : (
            <div className="flex items-center justify-center h-20 text-xs text-muted-foreground">
              No status data for this period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Content Section */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setSearch({ tab: v as 'overview' | 'parameters' })}
        className="flex-1 min-h-0 flex flex-col"
      >
        <TabsList className="shrink-0 w-full">
          <TabsTrigger value="overview" className="flex-1 text-xs">
            Overview
          </TabsTrigger>
          <TabsTrigger value="parameters" className="flex-1 text-xs">
            Parameters
          </TabsTrigger>
        </TabsList>

        {/* Overview: 3-column on desktop, tabs on mobile */}
        <TabsContent value="overview" className="flex-1 min-h-0 mt-2">
          <div className="hidden lg:grid lg:grid-cols-3 lg:h-full gap-3">
            <ScrollableCard
              title="Ticket History"
              icon={<HugeiconsIcon icon={Calendar02Icon} className="w-4 h-4 text-primary" />}
              iconBg="bg-primary/10"
              count={ticketsData?.pagination?.totalItems ?? tickets.length}
              loading={ticketsLoading}
            >
              <TicketList tickets={tickets} />
            </ScrollableCard>

            <ScrollableCard
              title="Parts Consumed"
              icon={
                <HugeiconsIcon
                  icon={Settings01Icon}
                  className="w-4 h-4 text-blue-600 dark:text-blue-400"
                />
              }
              iconBg="bg-blue-500/10"
              count={filteredParts.length}
              loading={partsLoading}
            >
              <PartsList parts={filteredParts} />
            </ScrollableCard>

            <ScrollableCard
              title="Upcoming PM"
              icon={
                <HugeiconsIcon
                  icon={Clock01Icon}
                  className="w-4 h-4 text-emerald-600 dark:text-emerald-400"
                />
              }
              iconBg="bg-emerald-500/10"
              count={scheduleList.length}
              loading={schedulesLoading}
            >
              <PmList schedules={scheduleList} />
            </ScrollableCard>
          </div>

          {/* Mobile: nested tabs for tickets/parts/PM */}
          <Tabs defaultValue="tickets" className="flex flex-col h-full lg:hidden">
            <TabsList className="shrink-0 w-full">
              <TabsTrigger value="tickets" className="flex-1 text-xs">
                Tickets ({ticketsData?.pagination?.totalItems ?? tickets.length})
              </TabsTrigger>
              <TabsTrigger value="parts" className="flex-1 text-xs">
                Parts ({filteredParts.length})
              </TabsTrigger>
              <TabsTrigger value="pm" className="flex-1 text-xs">
                PM ({scheduleList.length})
              </TabsTrigger>
            </TabsList>
            <TabsContent value="tickets" className="flex-1 min-h-0 mt-2">
              <Card className="h-full border-muted/50 bg-card/30">
                <ScrollArea className="h-full">
                  <div className="p-3">
                    {ticketsLoading ? <LoadingSpinner /> : <TicketList tickets={tickets} />}
                  </div>
                </ScrollArea>
              </Card>
            </TabsContent>
            <TabsContent value="parts" className="flex-1 min-h-0 mt-2">
              <Card className="h-full border-muted/50 bg-card/30">
                <ScrollArea className="h-full">
                  <div className="p-3">
                    {partsLoading ? <LoadingSpinner /> : <PartsList parts={filteredParts} />}
                  </div>
                </ScrollArea>
              </Card>
            </TabsContent>
            <TabsContent value="pm" className="flex-1 min-h-0 mt-2">
              <Card className="h-full border-muted/50 bg-card/30">
                <ScrollArea className="h-full">
                  <div className="p-3">
                    {schedulesLoading ? <LoadingSpinner /> : <PmList schedules={scheduleList} />}
                  </div>
                </ScrollArea>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Parameters Chart */}
        <TabsContent value="parameters" className="flex-1 min-h-0 mt-2">
          <Card className="h-full border-muted/50 bg-card/30 overflow-hidden">
            <ScrollArea className="h-full">
              <ParametersChartTab initialAssetId={assetId} />
            </ScrollArea>
          </Card>
        </TabsContent>
      </Tabs>

      {/* QR Dialog */}
      <AssetQrCard
        assetId={assetId}
        assetName={asset.name}
        assetCode={asset.code}
        open={showQrCard}
        onOpenChange={setShowQrCard}
      />
    </div>
  );
}

/* ── Status Timeline ─────────────────────────────────────────────── */

function StatusTimeline({
  segments,
  statusSummary,
  timeRange,
}: {
  segments: Array<{
    id: string;
    statusName: string;
    statusColor: string;
    startTime: string;
    endTime: string | null;
    durationSeconds: number | null;
  }>;
  statusSummary: Array<{
    statusName: string;
    statusColor: string;
    totalDurationSeconds: number;
    percentage: number;
    segmentCount: number;
  }>;
  timeRange: { start?: string; end?: string };
}) {
  const [hovered, setHovered] = useState<string | null>(null);
  const [mouse, setMouse] = useState<{ x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const now = new Date();

  const tlStart = timeRange.start
    ? new Date(timeRange.start)
    : new Date(segments[0]?.startTime || Date.now());
  const tlEnd = timeRange.end ? new Date(timeRange.end) : now;
  const totalMs = Math.max(1, tlEnd.getTime() - tlStart.getTime());

  interface TimelineBar {
    id: string;
    statusName: string;
    statusColor: string;
    left: number;
    width: number;
    durationMs: number;
    startTime: Date;
    endTime: Date;
  }

  const bars: TimelineBar[] = useMemo(() => {
    return segments
      .map((seg): TimelineBar | null => {
        const s = new Date(seg.startTime);
        const rawEnd = seg.endTime ? new Date(seg.endTime) : now;
        const e = rawEnd.getTime() > tlEnd.getTime() ? tlEnd : rawEnd;
        if (e < tlStart || s > tlEnd) return null;
        const cs = s < tlStart ? tlStart : s;
        const ce = e > tlEnd ? tlEnd : e;
        const dur = Math.max(0, ce.getTime() - cs.getTime());
        return {
          id: seg.id,
          statusName: seg.statusName,
          statusColor: seg.statusColor,
          left: ((cs.getTime() - tlStart.getTime()) / totalMs) * 100,
          width: Math.max(0.2, (dur / totalMs) * 100),
          durationMs: dur,
          startTime: cs,
          endTime: ce,
        };
      })
      .filter((b): b is TimelineBar => b !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments, tlStart.getTime(), tlEnd.getTime(), totalMs]);

  const sortedSummary = useMemo(
    () => [...statusSummary].sort((a, b) => b.totalDurationSeconds - a.totalDurationSeconds),
    [statusSummary],
  );

  return (
    <div className="space-y-2">
      {/* Timeline labels */}
      <div className="flex justify-between text-[10px] font-mono text-muted-foreground/60">
        <span>{format(tlStart, 'MMM d, HH:mm')}</span>
        <span>{format(new Date(tlStart.getTime() + totalMs / 2), 'MMM d, HH:mm')}</span>
        <span>{format(tlEnd, 'MMM d, HH:mm')}</span>
      </div>

      {/* Bar */}
      <div
        ref={containerRef}
        className="relative h-10 rounded-lg bg-muted/20 border border-border/50 overflow-hidden"
        onMouseMove={(e) => setMouse({ x: e.clientX, y: e.clientY })}
        onMouseLeave={() => {
          setHovered(null);
          setMouse(null);
        }}
      >
        {/* Grid lines */}
        <div className="absolute inset-0 flex pointer-events-none">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex-1 border-r border-border/30 last:border-r-0" />
          ))}
        </div>
        {/* Now marker */}
        {(() => {
          const n = now.getTime();
          if (n >= tlStart.getTime() && n <= tlEnd.getTime()) {
            const pos = ((n - tlStart.getTime()) / totalMs) * 100;
            return (
              <div
                className="absolute top-0 h-full w-px bg-foreground/40 z-20"
                style={{ left: `${pos}%` }}
              >
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-[9px] font-semibold bg-foreground text-background px-1 rounded">
                  now
                </div>
              </div>
            );
          }
          return null;
        })()}
        {/* Segments */}
        {bars.map((bar: TimelineBar) => (
          <div
            key={bar.id}
            className="absolute top-0 h-full transition-opacity duration-150 cursor-pointer hover:opacity-100"
            style={{
              left: `${bar.left}%`,
              width: `${bar.width}%`,
              backgroundColor: bar.statusColor || '#888',
              opacity: hovered === bar.id ? 1 : 0.75,
            }}
            onMouseEnter={() => setHovered(bar.id)}
          />
        ))}
      </div>

      {/* Tooltip - rendered via portal so it's above everything */}
      {hovered &&
        mouse &&
        (() => {
          const bar: TimelineBar | undefined = bars.find((b: TimelineBar) => b.id === hovered);
          if (!bar) return null;
          return createPortal(
            <div
              className="fixed pointer-events-none rounded-md px-3 py-2 text-xs bg-popover text-popover-foreground border shadow-xl"
              style={{
                left: mouse.x + 12,
                top: mouse.y - 8,
                transform: 'translateY(-100%)',
                zIndex: 9999,
              }}
            >
              <div className="font-semibold mb-0.5">{bar.statusName}</div>
              <div>Duration: {formatDuration(Math.floor(bar.durationMs / 1000))}</div>
              <div>
                {format(bar.startTime, 'HH:mm')} - {format(bar.endTime, 'HH:mm')}
              </div>
            </div>,
            document.body,
          );
        })()}

      {/* Summary pills */}
      <div className="flex items-center gap-3 flex-wrap">
        {sortedSummary.map((s) => (
          <div key={s.statusName} className="flex items-center gap-1.5 text-xs">
            <div
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: s.statusColor }}
            />
            <span className="font-medium">{s.statusName}</span>
            <span className="text-muted-foreground">
              {formatDuration(s.totalDurationSeconds)} ({s.percentage.toFixed(0)}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Scrollable Card (Desktop) ──────────────────────────────────── */

function ScrollableCard({
  title,
  icon,
  iconBg,
  count,
  loading,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  iconBg: string;
  count: number;
  loading: boolean;
  children: React.ReactNode;
}) {
  return (
    <Card className="border-muted/50 bg-card/30 backdrop-blur-sm overflow-hidden flex flex-col">
      <CardHeader className="pb-2 pt-3 px-3 shrink-0">
        <CardTitle className="flex items-center gap-2 text-sm">
          <div className={cn('w-6 h-6 rounded-md flex items-center justify-center', iconBg)}>
            {icon}
          </div>
          {title}
          <Badge variant="outline" className="text-[10px] ml-auto">
            {count}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 px-0 pb-0">
        <ScrollArea className="h-full">
          <div className="px-3 pb-3">{loading ? <LoadingSpinner /> : children}</div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

/* ── Ticket List ────────────────────────────────────────────────── */

function TicketList({ tickets }: { tickets: any[] }) {
  const navigate = useNavigate();
  if (tickets.length === 0) {
    return <EmptyState icon={Calendar02Icon} text="No tickets in this period" />;
  }
  return (
    <div className="space-y-1.5">
      {tickets.map((ticket) => (
        <button
          type="button"
          key={ticket.id}
          className="flex items-start gap-2.5 p-2 rounded-lg border border-border/40 hover:bg-accent/40 transition-colors text-left w-full"
          onClick={() =>
            navigate({
              to: '/tickets/$ticketId',
              params: { ticketId: ticket.id },
            })
          }
        >
          <div
            className={cn(
              'w-1.5 h-1.5 rounded-full mt-1.5 shrink-0',
              ticket.status === 'completed' && 'bg-emerald-500',
              ticket.status === 'in_progress' && 'bg-amber-500',
              ticket.status === 'open' && 'bg-rose-500',
              !['completed', 'in_progress', 'open'].includes(ticket.status) && 'bg-muted',
            )}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-medium truncate">{ticket.title}</p>
              <Badge
                variant="outline"
                className={cn(
                  'text-[9px] capitalize shrink-0 px-1.5 py-0',
                  ticket.status === 'completed' &&
                    'border-emerald-500/30 text-emerald-600 dark:text-emerald-400',
                  ticket.status === 'in_progress' &&
                    'border-amber-500/30 text-amber-600 dark:text-amber-400',
                  ticket.status === 'open' && 'border-rose-500/30 text-rose-600 dark:text-rose-400',
                )}
              >
                {ticket.status?.replace('_', ' ') || 'open'}
              </Badge>
            </div>
            <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
              {ticket.createdAt && (
                <span>{formatDistanceToNow(new Date(ticket.createdAt), { addSuffix: true })}</span>
              )}
              {(ticket as any).assignedToName && (
                <>
                  <span>·</span>
                  <span>{(ticket as any).assignedToName}</span>
                </>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function PartsList({ parts }: { parts: any[] }) {
  if (parts.length === 0) {
    return <EmptyState icon={Settings01Icon} text="No parts consumed in this period" />;
  }
  return (
    <div className="space-y-1.5">
      {parts.map((part) => {
        const ext = part as any;
        return (
          <div
            key={part.id}
            className="flex items-center gap-2.5 p-2 rounded-lg border border-border/40 hover:bg-accent/40 transition-colors"
          >
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{ext.partItemName || 'Unknown Part'}</p>
              <p className="text-[10px] text-muted-foreground font-mono">
                {ext.partItemCode || 'N/A'}
                {ext.batchNumber && ` · ${ext.batchNumber}`}
              </p>
            </div>
            <div className="text-right shrink-0">
              <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                {part.quantity}
              </span>
              <p className="text-[9px] text-muted-foreground uppercase">qty</p>
            </div>
            <div className="text-[10px] text-muted-foreground shrink-0">
              {part.installedAt
                ? formatDistanceToNow(new Date(part.installedAt), { addSuffix: true })
                : ''}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── PM Schedule List ───────────────────────────────────────────── */

function PmList({ schedules }: { schedules: any[] }) {
  const navigate = useNavigate();
  if (schedules.length === 0) {
    return <EmptyState icon={Clock01Icon} text="No upcoming PM schedules" />;
  }
  return (
    <div className="space-y-1.5">
      {schedules.map((schedule) => (
        <button
          type="button"
          key={schedule.id}
          className="p-2 rounded-lg border border-border/40 hover:bg-accent/40 transition-colors text-left w-full"
          onClick={() => {
            const date = schedule.startDate ? new Date(schedule.startDate) : new Date();
            navigate({
              to: '/preventive-maintenance/calendar',
              search: {
                month: String(date.getMonth() + 1),
                year: String(date.getFullYear()),
              },
            });
          }}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs font-medium truncate">{schedule.name}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {getFrequencyLabel(schedule.scheduleConfig)}
              </p>
            </div>
            <Badge
              variant="outline"
              className={cn(
                'text-[9px] capitalize shrink-0 px-1.5 py-0',
                getPriorityColor(schedule.priority),
              )}
            >
              {schedule.priority || 'medium'}
            </Badge>
          </div>
          <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
            {schedule.effortLevel && (
              <span className="px-1.5 py-0.5 rounded bg-muted/50">{schedule.effortLevel}</span>
            )}
            {schedule.startDate && (
              <span>Starts {format(new Date(schedule.startDate), 'MMM d')}</span>
            )}
            {schedule.assignedToUser && (
              <span className="ml-auto">{schedule.assignedToUser.name}</span>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}

/* ── Shared Helpers ─────────────────────────────────────────────── */
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-8">
      <HugeiconsIcon
        icon={Loading01Icon}
        className="w-5 h-5 animate-spin text-muted-foreground/40"
      />
    </div>
  );
}

function EmptyState({ icon, text }: { icon: any; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
      <HugeiconsIcon icon={icon} className="w-8 h-8 mb-2 text-muted-foreground/30" />
      <p className="text-xs">{text}</p>
    </div>
  );
}

function getFrequencyLabel(config: any) {
  if (!config?.frequency) return 'Unknown';
  switch (config.frequency) {
    case 'daily':
      return `Daily at ${config.timeOfDay || '—'}`;
    case 'weekly': {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return `Weekly on ${days[config.dayOfWeek] || '—'} at ${config.timeOfDay || '—'}`;
    }
    case 'monthly':
      return `Monthly on day ${config.dayOfMonth} at ${config.timeOfDay || '—'}`;
    case 'yearly': {
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      return `Yearly on ${months[config.month - 1] || '—'} ${config.dayOfMonth}`;
    }
    case 'custom':
      return `Every ${config.interval} ${config.unit}${config.interval > 1 ? 's' : ''}`;
    case 'one-time':
      return `One-time on ${config.scheduledDate || '—'}`;
    case 'usage':
      return `Usage ${config.operator} ${config.threshold}`;
    case 'runtime':
      return `Every ${config.thresholdHours} hours`;
    default:
      return config.frequency;
  }
}

function getPriorityColor(priority: string) {
  const p = priority?.toLowerCase() || '';
  if (p === 'critical')
    return 'text-rose-600 dark:text-rose-400 bg-rose-500/10 border-rose-200/50 dark:border-rose-800/50';
  if (p === 'high')
    return 'text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-200/50 dark:border-amber-800/50';
  if (p === 'medium')
    return 'text-blue-600 dark:text-blue-400 bg-blue-500/10 border-blue-200/50 dark:border-blue-800/50';
  return 'text-slate-600 dark:text-slate-400 bg-slate-500/10 border-slate-200/50 dark:border-slate-800/50';
}

export const Route = createFileRoute('/_authenticated/asset/$assetId')({
  component: AssetDetailPage,
  validateSearch: (search): AssetDetailSearch => assetDetailSearchSchema.parse(search),
});
